/**
 * RevenueCat Subscription Screen
 *
 * Modern subscription UI using RevenueCat for mobile platforms
 * Automatically handles iOS App Store and Google Play billing
 */

import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  SafeAreaView,
  Linking,
  StyleSheet,
  Platform
} from 'react-native';
import { PurchasesPackage, PurchasesStoreProduct } from 'react-native-purchases';
import tw from 'twrnc';

import { t } from '../../../i18n/translations';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { getBillingInterval } from '../config/revenueCatProducts';
import {
  useOfferings,
  usePurchase,
  useRestorePurchases,
  useSubscriptionStatus
} from '../hooks/useRevenueCat';
import { revenueCatService } from '../services/revenueCatService';

// The store returns plan names/descriptions in the *device* language; the app language can differ
// (e.g. Korean in-app on an English phone). Use the app's own copy for the two known plans and
// fall back to the store text for any other product.
const localizedPlan = (
  product: { identifier: string; title: string; description: string },
  lang: string,
): { title: string; description: string } => {
  const id = product.identifier.toLowerCase();
  const kind = id.includes('family.annual') ? 'Annual' : id.includes('family.monthly') ? 'Monthly' : null;
  if (!kind) return { title: product.title, description: product.description };
  const titleKey = `monetization.planTitle${kind}`;
  const descKey = `monetization.planDesc${kind}`;
  const title = t(titleKey, lang);
  const description = t(descKey, lang);
  return {
    title: title === titleKey ? product.title : title,
    description: description === descKey ? product.description : description,
  };
};

/** Replace `{placeholder}` tokens in a translated template with concrete values. */
const interp = (template: string, values: Record<string, string | number>): string =>
  Object.entries(values).reduce(
    (s, [k, v]) => s.replace(`{${k}}`, String(v)),
    template,
  );

const planSortRank = (productId: string): number =>
  getBillingInterval(productId) === 'monthly' ? 0 : 1;

const sameCurrency = (
  monthly: { currencyCode?: string },
  annual: { currencyCode?: string },
): boolean => {
  if (!monthly.currencyCode || !annual.currencyCode) {
    return true;
  }

  return monthly.currencyCode === annual.currencyCode;
};

const shellColors = {
  canvas: '#120A2B',
  panel: '#080B24',
  panelSoft: 'rgba(14, 18, 48, 0.92)',
  gold: '#F8C84E',
  goldSoft: 'rgba(248, 200, 78, 0.18)',
  purple: '#6D35A8',
  purpleSoft: 'rgba(109, 53, 168, 0.35)',
  ivory: '#FFF7DA',
  muted: '#D9D2C2',
  green: '#153F27',
  red: '#681E25',
  line: 'rgba(248, 200, 78, 0.38)',
};

interface RevenueCatSubscriptionScreenProps {
  route?: {
    params?: {
      preselectedPlan?: string;
      preselectedProductId?: string;
      resumeAfterAuth?: boolean;
    };
  };
  onSuccess?: () => void;
  onClose?: () => void;
}

export const RevenueCatSubscriptionScreen: React.FC<RevenueCatSubscriptionScreenProps> = ({
  route,
  onSuccess,
  onClose
}) => {
  const [selectedPlan, setSelectedPlan] = useState<
    | { kind: 'package'; pkg: PurchasesPackage }
    | { kind: 'product'; product: PurchasesStoreProduct }
    | null
  >(null);
  const [purchasingProduct, setPurchasingProduct] = useState(false);
  const { language } = useLanguageStore();

  // Hooks
  const { currentOffering, fallbackProducts, loading: offeringsLoading, error: offeringsError, refresh: refreshOfferings } = useOfferings();
  const { subscriptionInfo, loading: statusLoading, refresh: refreshStatus } = useSubscriptionStatus();
  const { purchase, purchasing } = usePurchase();
  const { restore, restoring } = useRestorePurchases();

  const loading = offeringsLoading || statusLoading;

  const visiblePackages = React.useMemo(() => {
    return [...(currentOffering?.packages || [])].sort((a, b) => {
      return planSortRank(a.product.identifier) - planSortRank(b.product.identifier);
    });
  }, [currentOffering?.packages]);
  const hasPackages = visiblePackages.length > 0;

  const filteredFallbackProducts = React.useMemo(() => (fallbackProducts || []).filter((p) => {
    return !p.identifier.toLowerCase().includes('group_');
  }).sort((a, b) => {
    return planSortRank(a.identifier) - planSortRank(b.identifier);
  }), [fallbackProducts]);
  const hasFallbackProducts = filteredFallbackProducts.length > 0;
  const hasPlans = hasPackages || hasFallbackProducts;
  const isPurchasing = purchasing || purchasingProduct;
  const preselectedProductId = route?.params?.preselectedProductId;
  const selectedPlanProduct = selectedPlan?.kind === 'package'
    ? selectedPlan.pkg.product
    : selectedPlan?.product;
  const selectedPlanIsAnnual = selectedPlanProduct
    ? getBillingInterval(selectedPlanProduct.identifier) === 'annual'
    : false;
  const selectedPlanPeriod = selectedPlanIsAnnual
    ? t('monetization.perYear', language)
    : t('monetization.perMonth', language);

  // Compute annual savings from actual product prices (parent owns the data)
  const annualSavingsPercent = React.useMemo(() => {
    if (hasPackages) {
      const monthly = visiblePackages.find((p) => getBillingInterval(p.product.identifier) === 'monthly');
      const annual = visiblePackages.find((p) => getBillingInterval(p.product.identifier) === 'annual');
      if (monthly && annual && monthly.product.price > 0 && sameCurrency(monthly.product, annual.product)) {
        return Math.round((1 - annual.product.price / (monthly.product.price * 12)) * 100);
      }
    }
    if (hasFallbackProducts) {
      const monthly = filteredFallbackProducts.find((p) => getBillingInterval(p.identifier) === 'monthly');
      const annual = filteredFallbackProducts.find((p) => getBillingInterval(p.identifier) === 'annual');
      if (monthly && annual && monthly.price > 0 && sameCurrency(monthly, annual)) {
        return Math.round((1 - annual.price / (monthly.price * 12)) * 100);
      }
    }
    return null;
  }, [hasPackages, visiblePackages, hasFallbackProducts, filteredFallbackProducts]);

  React.useEffect(() => {
    if (selectedPlan || !preselectedProductId) {
      return;
    }

    const matchingPackage = visiblePackages.find(
      (pkg) => pkg.product.identifier === preselectedProductId
    );
    if (matchingPackage) {
      setSelectedPlan({ kind: 'package', pkg: matchingPackage });
      return;
    }

    const matchingProduct = filteredFallbackProducts.find(
      (product) => product.identifier === preselectedProductId
    );
    if (matchingProduct) {
      setSelectedPlan({ kind: 'product', product: matchingProduct });
    }
  }, [visiblePackages, filteredFallbackProducts, preselectedProductId, selectedPlan]);

  /**
   * Handle package purchase
   */
  const handlePurchase = async (pkg: PurchasesPackage) => {
    try {
      console.log('🛒 Starting purchase flow:', pkg.product.identifier);

      const result = await purchase(pkg);

      if (result.success) {
        // Refresh subscription status
        await refreshStatus();

        Alert.alert(
          t('monetization.purchaseSuccessTitle', language),
          t('monetization.purchaseSuccessMessage', language),
          [
            {
              text: t('monetization.ok', language),
              onPress: () => {
                if (onSuccess) {
                  onSuccess();
                } else if (onClose) {
                  onClose();
                }
              }
            }
          ]
        );
      } else if (!result.userCancelled) {
        Alert.alert(
          t('monetization.purchaseFailedTitle', language),
          result.error || t('monetization.purchaseFailedMessage', language),
          [{ text: t('monetization.ok', language) }]
        );
      }
    } catch (error) {
      console.error('Purchase error:', error);
      Alert.alert(
        t('monetization.purchaseErrorTitle', language),
        t('monetization.purchaseErrorMessage', language),
        [{ text: t('monetization.ok', language) }]
      );
    }
  };

  /**
   * Handle store product purchase (fallback path)
   */
  const handlePurchaseProduct = async (product: PurchasesStoreProduct) => {
    try {
      setPurchasingProduct(true);
      console.log('🛒 Starting fallback purchase flow:', product.identifier);

      const result = await revenueCatService.purchaseStoreProduct(product);

      if (result.success) {
        await refreshStatus();
        Alert.alert(
          t('monetization.purchaseSuccessTitle', language),
          t('monetization.purchaseSuccessMessage', language),
          [
            {
              text: t('monetization.ok', language),
              onPress: () => {
                if (onSuccess) {
                  onSuccess();
                } else if (onClose) {
                  onClose();
                }
              }
            }
          ]
        );
      } else if (!result.userCancelled) {
        Alert.alert(
          t('monetization.purchaseFailedTitle', language),
          result.error || t('monetization.purchaseFailedMessage', language),
          [{ text: t('monetization.ok', language) }]
        );
      }
    } catch (error) {
      console.error('Fallback purchase error:', error);
      Alert.alert(
        t('monetization.purchaseErrorTitle', language),
        t('monetization.purchaseErrorMessage', language),
        [{ text: t('monetization.ok', language) }]
      );
    } finally {
      setPurchasingProduct(false);
    }
  };

  /**
   * Handle restore purchases
   */
  const handleRestore = async () => {
    try {
      console.log('🔄 Restoring purchases...');

      const result = await restore();

      if (result.success) {
        // Refresh subscription status
        await refreshStatus();

        const hasActiveSubscription = result.customerInfo &&
          Object.keys(result.customerInfo.entitlements.active).length > 0;

        if (hasActiveSubscription) {
          Alert.alert(
            t('monetization.restoreSuccessTitle', language),
            t('monetization.restoreSuccessMessage', language),
            [{ text: t('monetization.ok', language) }]
          );
        } else {
          Alert.alert(
            t('monetization.noRestoredTitle', language),
            t('monetization.noRestoredMessage', language),
            [{ text: t('monetization.ok', language) }]
          );
        }
      } else {
        Alert.alert(
          t('monetization.restoreFailedTitle', language),
          result.error || t('monetization.restoreFailedMessage', language),
          [{ text: t('monetization.ok', language) }]
        );
      }
    } catch (error) {
      console.error('Restore error:', error);
      Alert.alert(
        t('monetization.restoreErrorTitle', language),
        t('monetization.restoreErrorMessage', language),
        [{ text: t('monetization.ok', language) }]
      );
    }
  };

  /**
   * Handle manage subscription
   */
  const handleManageSubscription = async () => {
    try {
      const url = await revenueCatService.getManagementURL();
      if (url) {
        await Linking.openURL(url);
      }
    } catch (error) {
      console.error('Failed to open management URL:', error);
      Alert.alert(
        t('monetization.errorTitle', language),
        t('monetization.errorGenericMessage', language),
        [{ text: t('monetization.ok', language) }]
      );
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={paywallStyles.screen}>
        <View style={tw`flex-1 items-center justify-center`}>
          <ActivityIndicator size="large" color={shellColors.gold} />
          <Text style={paywallStyles.loadingText}>{t('monetization.loadingPlans', language)}</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Error state - offerings failed to load
  if (offeringsError && !hasFallbackProducts) {
    return (
      <SafeAreaView style={paywallStyles.screen}>
        <View style={paywallStyles.header}>
          <Text style={paywallStyles.headerTitle}>{t('monetization.premiumPlansTitle', language)}</Text>
          {onClose && (
            <TouchableOpacity onPress={onClose} style={tw`p-2`}>
              <Ionicons name="close" size={24} color={shellColors.gold} />
            </TouchableOpacity>
          )}
        </View>
        <View style={tw`flex-1 items-center justify-center px-6`}>
          <Ionicons name="cloud-offline-outline" size={64} color={shellColors.gold} />
          <Text style={paywallStyles.emptyTitle}>
            {t('monetization.loadFailedTitle', language)}
          </Text>
          <Text style={paywallStyles.emptyBody}>
            {t('monetization.loadFailedMessage', language)}
          </Text>
          <Text style={paywallStyles.emptyMeta}>
            Error: {offeringsError}
          </Text>
          <TouchableOpacity
            onPress={refreshOfferings}
            style={paywallStyles.goldButton}
          >
            <Text style={paywallStyles.goldButtonText}>{t('monetization.tryAgain', language)}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleRestore}
            disabled={restoring}
            style={tw`mt-4 py-3`}
          >
            <Text style={paywallStyles.restoreText}>
              {restoring ? t('monetization.restoring', language) : t('monetization.restorePurchases', language)}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Empty state - offerings loaded but no packages available
  if (!hasPlans && !subscriptionInfo?.isActive) {
    return (
      <SafeAreaView style={paywallStyles.screen}>
        <View style={paywallStyles.header}>
          <Text style={paywallStyles.headerTitle}>
            {t('monetization.premiumPlansTitle', language)}
          </Text>
          {onClose && (
            <TouchableOpacity onPress={onClose} style={tw`p-2`}>
              <Ionicons name="close" size={24} color={shellColors.gold} />
            </TouchableOpacity>
          )}
        </View>
        <View style={tw`flex-1 items-center justify-center px-6`}>
          <Ionicons name="pricetag-outline" size={64} color={shellColors.gold} />
          <Text style={paywallStyles.emptyTitle}>
            {t('monetization.plansComingSoonTitle', language)}
          </Text>
          <Text style={paywallStyles.emptyBody}>
            {t('monetization.plansComingSoonMessage', language)}
          </Text>
          <TouchableOpacity
            onPress={refreshOfferings}
            style={paywallStyles.goldButton}
          >
            <Text style={paywallStyles.goldButtonText}>{t('monetization.refresh', language)}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleRestore}
            disabled={restoring}
            style={tw`mt-2 py-3`}
          >
            <Text style={paywallStyles.restoreText}>
              {restoring ? t('monetization.restoring', language) : t('monetization.restorePurchases', language)}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={paywallStyles.screen}>
      {/* Header */}
      <View style={paywallStyles.header}>
        <Text style={paywallStyles.headerTitle}>
          {t('monetization.premiumPlansTitle', language)}
        </Text>
        {onClose && (
          <TouchableOpacity onPress={onClose} style={tw`p-2`}>
            <Ionicons name="close" size={24} color={shellColors.gold} />
          </TouchableOpacity>
        )}
      </View>

      <ScrollView style={tw`flex-1`} contentContainerStyle={paywallStyles.content}>
        <View style={paywallStyles.heroCard}>
          <View style={paywallStyles.heroGlow} />
          <View style={paywallStyles.heroIcon}>
            <Ionicons name="diamond" size={28} color={shellColors.gold} />
          </View>
          <Text style={paywallStyles.heroEyebrow}>{t('monetization.premiumPlansTitle', language)}</Text>
          <Text style={paywallStyles.heroTitle}>{t('monetization.premiumPlansTitle', language)}</Text>
          <Text style={paywallStyles.heroSubtitle}>{t('monetization.autoRenewalTerms', language)}</Text>
        </View>

        {/* Current Subscription Banner */}
        {subscriptionInfo?.isActive && (
          <View style={paywallStyles.activeBanner}>
            <View style={tw`flex-row items-center mb-2`}>
              <Ionicons name="checkmark-circle" size={20} color={shellColors.gold} />
              <Text style={paywallStyles.activeTitle}>{t('monetization.activeSubscription', language)}</Text>
            </View>
            <Text style={paywallStyles.activeBody}>
              {interp(t('monetization.planLabel', language), { tier: subscriptionInfo.tier.replace('_', ' ').toUpperCase() })}
            </Text>
            <Text style={paywallStyles.activeMeta}>
              {subscriptionInfo.willRenew
                ? interp(t('monetization.renewsDate', language), { date: subscriptionInfo.expirationDate?.toLocaleDateString() || '' })
                : interp(t('monetization.expiresDate', language), { date: subscriptionInfo.expirationDate?.toLocaleDateString() || '' })
              }
            </Text>
            <TouchableOpacity
              onPress={handleManageSubscription}
              style={paywallStyles.manageButton}
            >
              <Text style={paywallStyles.manageButtonText}>{t('monetization.manageSubscription', language)}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Error State */}
        {offeringsError && !hasPackages && (
          <View style={paywallStyles.noticeCard}>
            <Ionicons name="alert-circle" size={48} color={shellColors.gold} />
            <Text style={paywallStyles.emptyTitle}>
              {t('monetization.loadFailedTitle', language)}
            </Text>
            <Text style={paywallStyles.emptyBody}>
              {t('monetization.loadFailedMessage', language)}
            </Text>
            <TouchableOpacity
              onPress={refreshOfferings}
              style={paywallStyles.goldButton}
            >
              <Text style={paywallStyles.goldButtonText}>{t('monetization.tryAgain', language)}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Empty State - offerings loaded but no packages available AND no fallback products */}
        {!offeringsLoading && !offeringsError && !hasPackages && !hasFallbackProducts && (
          <View style={paywallStyles.noticeCard}>
            <Ionicons name="information-circle" size={48} color={shellColors.gold} />
              <Text style={paywallStyles.emptyTitle}>
              {t('monetization.noPlansTitle', language)}
              </Text>
            <Text style={paywallStyles.emptyBody}>
              {t('monetization.noPlansMessage', language)}
            </Text>
            <TouchableOpacity
              onPress={refreshOfferings}
              style={paywallStyles.goldButton}
            >
              <Text style={paywallStyles.goldButtonText}>{t('monetization.refresh', language)}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Available Packages */}
        {hasPackages && visiblePackages.map((pkg) => (
          <PackageCard
            key={pkg.product.identifier}
            package={pkg}
            selected={selectedPlan?.kind === 'package' && selectedPlan.pkg.product.identifier === pkg.product.identifier}
            onSelect={() => setSelectedPlan({ kind: 'package', pkg })}
            currentPlan={subscriptionInfo?.productIdentifier === pkg.product.identifier}
            popular={getBillingInterval(pkg.product.identifier) === 'monthly'}
            savingsPercent={annualSavingsPercent}
            language={language}
          />
        ))}

        {/* Fallback Products (when offerings are not available) */}
        {!hasPackages && hasFallbackProducts && filteredFallbackProducts.map((product) => (
          <StoreProductCard
            key={product.identifier}
            product={product}
            selected={selectedPlan?.kind === 'product' && selectedPlan.product.identifier === product.identifier}
            onSelect={() => setSelectedPlan({ kind: 'product', product })}
            currentPlan={subscriptionInfo?.productIdentifier === product.identifier}
            popular={getBillingInterval(product.identifier) === 'monthly'}
            savingsPercent={annualSavingsPercent}
            language={language}
          />
        ))}

        {/* Restore Purchases Button */}
        <TouchableOpacity
          onPress={handleRestore}
          disabled={restoring}
          style={paywallStyles.restoreButton}
        >
          <Text style={paywallStyles.restoreText}>
            {restoring ? t('monetization.restoring', language) : t('monetization.restorePurchases', language)}
          </Text>
        </TouchableOpacity>

        {/* Terms & Privacy */}
        <View style={paywallStyles.termsBlock}>
          <Text style={paywallStyles.termsText}>
            {t('monetization.autoRenewalTerms', language)}
          </Text>
          <View style={tw`flex-row justify-center mt-3 gap-4`}>
            <TouchableOpacity onPress={() => Linking.openURL('https://kingdombiblequiz.com/terms')}>
              <Text style={paywallStyles.termsLink}>{t('monetization.termsLink', language)}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => Linking.openURL('https://kingdombiblequiz.com/privacy')}>
              <Text style={paywallStyles.termsLink}>{t('monetization.privacyLink', language)}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Subscribe Button */}
      {!subscriptionInfo?.isActive && hasPlans && (
        <View style={paywallStyles.subscribeDock}>
          {selectedPlanProduct && (
            <Text style={paywallStyles.disclosureText}>
              {interp(t('monetization.selectedPlanRenewalDisclosure', language), {
                title: localizedPlan(selectedPlanProduct, language).title,
                price: selectedPlanProduct.priceString,
                period: selectedPlanPeriod,
              })}
            </Text>
          )}
          <TouchableOpacity
            style={[
              tw`bg-blue-600 py-4 rounded-lg`,
              paywallStyles.subscribeButton,
              (!selectedPlan || isPurchasing) && tw`opacity-50`
            ]}
            onPress={() => {
              if (!selectedPlan) return;
              if (selectedPlan.kind === 'package') {
                handlePurchase(selectedPlan.pkg);
              } else {
                handlePurchaseProduct(selectedPlan.product);
              }
            }}
            disabled={!selectedPlan || isPurchasing}
          >
            {isPurchasing ? (
              <ActivityIndicator color="white" />
            ) : (
              <Text style={paywallStyles.subscribeButtonText}>
                {t('monetization.subscribeNow', language)}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
};

/**
 * Package Card Component
 */
interface PackageCardProps {
  package: PurchasesPackage;
  selected: boolean;
  onSelect: () => void;
  currentPlan?: boolean;
  popular?: boolean;
  savingsPercent?: number | null;
  language?: string;
}

const PackageCard: React.FC<PackageCardProps> = ({
  package: pkg,
  selected,
  onSelect,
  currentPlan,
  popular,
  savingsPercent,
  language: lang = 'en',
}) => {
  const isAnnual = getBillingInterval(pkg.product.identifier) === 'annual';
  const savingsText = isAnnual && savingsPercent && savingsPercent > 0
    ? interp(t('monetization.savingsPercent', lang), { percent: savingsPercent })
    : null;

  return (
    <TouchableOpacity
      style={[
        paywallStyles.planCard,
        selected && paywallStyles.planCardSelected,
        popular && paywallStyles.planCardPopular
      ]}
      onPress={onSelect}
    >
      {/* Popular Badge */}
      {popular && (
        <View style={paywallStyles.popularBadge}>
          <Text style={paywallStyles.popularBadgeText}>{t('monetization.mostPopular', lang)}</Text>
        </View>
      )}

      {/* Current Plan Badge */}
      {currentPlan && (
        <View style={paywallStyles.currentBadge}>
          <Text style={paywallStyles.currentBadgeText}>{t('monetization.currentPlan', lang)}</Text>
        </View>
      )}

      {/* Package Header */}
      <View style={tw`mb-4`}>
        <Text style={paywallStyles.planTitle}>
          {localizedPlan(pkg.product, lang).title}
        </Text>
        <Text style={paywallStyles.planDescription}>
          {localizedPlan(pkg.product, lang).description}
        </Text>
      </View>

      {/* Pricing */}
      <View style={tw`flex-row items-baseline mb-4`}>
        <Text style={paywallStyles.planPrice}>
          {pkg.product.priceString}
        </Text>
        <Text style={paywallStyles.planPeriod}>
          {isAnnual ? t('monetization.perYear', lang) : t('monetization.perMonth', lang)}
        </Text>
        {savingsText && (
          <View style={paywallStyles.savingsBadge}>
            <Text style={paywallStyles.savingsBadgeText}>{savingsText}</Text>
          </View>
        )}
      </View>

      {/* Intro Price */}
      {pkg.product.introPrice && (
        <View style={paywallStyles.introCard}>
          <Text style={paywallStyles.introText}>
            {interp(t('monetization.introTrialLabel', lang), {
              price: pkg.product.introPrice.priceString,
              period: pkg.product.introPrice.period,
            })}
          </Text>
        </View>
      )}

      {/* Select Indicator */}
      <View style={tw`flex-row items-center justify-end`}>
        {selected && (
          <View style={tw`flex-row items-center`}>
            <Ionicons name="checkmark-circle" size={20} color={shellColors.gold} />
            <Text style={paywallStyles.selectedText}>{t('monetization.selected', lang)}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

export default RevenueCatSubscriptionScreen;

/**
 * Store Product Card (fallback)
 */
interface StoreProductCardProps {
  product: PurchasesStoreProduct;
  selected: boolean;
  onSelect: () => void;
  currentPlan?: boolean;
  popular?: boolean;
  savingsPercent?: number | null;
  language?: string;
}

const StoreProductCard: React.FC<StoreProductCardProps> = ({
  product,
  selected,
  onSelect,
  currentPlan,
  popular,
  savingsPercent,
  language: lang = 'en',
}) => {
  const isAnnual = getBillingInterval(product.identifier) === 'annual';
  const savingsText = isAnnual && savingsPercent && savingsPercent > 0
    ? interp(t('monetization.savingsPercent', lang), { percent: savingsPercent })
    : null;

  return (
    <TouchableOpacity
      style={[
        paywallStyles.planCard,
        selected && paywallStyles.planCardSelected,
        popular && paywallStyles.planCardPopular
      ]}
      onPress={onSelect}
    >
      {popular && (
        <View style={paywallStyles.popularBadge}>
          <Text style={paywallStyles.popularBadgeText}>{t('monetization.mostPopular', lang)}</Text>
        </View>
      )}

      {currentPlan && (
        <View style={paywallStyles.currentBadge}>
          <Text style={paywallStyles.currentBadgeText}>{t('monetization.currentPlan', lang)}</Text>
        </View>
      )}

      <View style={tw`mb-4`}>
        <Text style={paywallStyles.planTitle}>
          {localizedPlan(product, lang).title}
        </Text>
        <Text style={paywallStyles.planDescription}>
          {localizedPlan(product, lang).description}
        </Text>
      </View>

      <View style={tw`flex-row items-baseline mb-4`}>
        <Text style={paywallStyles.planPrice}>
          {product.priceString}
        </Text>
        <Text style={paywallStyles.planPeriod}>
          {isAnnual ? t('monetization.perYear', lang) : t('monetization.perMonth', lang)}
        </Text>
        {savingsText && (
          <View style={paywallStyles.savingsBadge}>
            <Text style={paywallStyles.savingsBadgeText}>{savingsText}</Text>
          </View>
        )}
      </View>

      <View style={tw`flex-row items-center justify-end`}>
        {selected && (
          <View style={tw`flex-row items-center`}>
            <Ionicons name="checkmark-circle" size={20} color={shellColors.gold} />
            <Text style={paywallStyles.selectedText}>{t('monetization.selected', lang)}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const paywallStyles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: shellColors.canvas,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    backgroundColor: shellColors.panel,
    borderBottomWidth: 1,
    borderBottomColor: shellColors.line,
  },
  headerTitle: {
    color: shellColors.ivory,
    fontSize: 21,
    fontWeight: '900',
  },
  content: {
    padding: 18,
    paddingBottom: 32,
  },
  loadingText: {
    marginTop: 16,
    color: shellColors.gold,
    fontSize: 16,
    fontWeight: '800',
  },
  heroCard: {
    borderRadius: 24,
    padding: 22,
    marginBottom: 18,
    overflow: 'hidden',
    backgroundColor: shellColors.panel,
    borderWidth: 1,
    borderColor: shellColors.line,
    ...Platform.select({
      ios: {
        shadowColor: shellColors.gold,
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.2,
        shadowRadius: 24,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0px 16px 30px rgba(248, 200, 78, 0.16)',
      },
    }),
  },
  heroGlow: {
    position: 'absolute',
    right: -74,
    top: -80,
    width: 190,
    height: 190,
    borderRadius: 95,
    backgroundColor: shellColors.purpleSoft,
  },
  heroIcon: {
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: shellColors.goldSoft,
    borderWidth: 1,
    borderColor: shellColors.line,
    marginBottom: 16,
  },
  heroEyebrow: {
    color: shellColors.gold,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  heroTitle: {
    color: shellColors.ivory,
    fontSize: 34,
    lineHeight: 40,
    fontWeight: '900',
    marginBottom: 10,
  },
  heroSubtitle: {
    color: shellColors.muted,
    fontSize: 13,
    lineHeight: 20,
  },
  activeBanner: {
    padding: 16,
    borderRadius: 18,
    marginBottom: 18,
    backgroundColor: shellColors.green,
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  activeTitle: {
    color: shellColors.ivory,
    fontWeight: '900',
    marginLeft: 8,
  },
  activeBody: {
    color: shellColors.ivory,
  },
  activeMeta: {
    color: shellColors.muted,
    fontSize: 13,
    marginTop: 4,
  },
  manageButton: {
    alignSelf: 'flex-start',
    marginTop: 12,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: shellColors.gold,
  },
  manageButtonText: {
    color: '#24123F',
    fontWeight: '900',
  },
  noticeCard: {
    padding: 22,
    borderRadius: 18,
    marginBottom: 18,
    alignItems: 'center',
    backgroundColor: shellColors.panelSoft,
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  emptyTitle: {
    color: shellColors.ivory,
    fontSize: 18,
    lineHeight: 24,
    fontWeight: '900',
    marginTop: 14,
    textAlign: 'center',
  },
  emptyBody: {
    color: shellColors.muted,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
  },
  emptyMeta: {
    color: 'rgba(217, 210, 194, 0.58)',
    textAlign: 'center',
    marginTop: 8,
    fontSize: 11,
  },
  goldButton: {
    marginTop: 20,
    borderRadius: 999,
    paddingHorizontal: 28,
    paddingVertical: 13,
    backgroundColor: shellColors.gold,
  },
  goldButtonText: {
    color: '#24123F',
    fontWeight: '900',
    fontSize: 15,
  },
  planCard: {
    padding: 22,
    borderRadius: 20,
    marginBottom: 16,
    backgroundColor: shellColors.panelSoft,
    borderWidth: 1.5,
    borderColor: shellColors.line,
    overflow: 'visible',
  },
  planCardSelected: {
    borderColor: shellColors.gold,
    backgroundColor: 'rgba(248, 200, 78, 0.12)',
  },
  planCardPopular: {
    borderColor: shellColors.gold,
  },
  popularBadge: {
    position: 'absolute',
    top: -12,
    left: 18,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 5,
    backgroundColor: shellColors.green,
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  popularBadgeText: {
    color: shellColors.ivory,
    fontSize: 11,
    fontWeight: '900',
  },
  currentBadge: {
    position: 'absolute',
    top: -12,
    right: 18,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 5,
    backgroundColor: shellColors.purple,
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  currentBadgeText: {
    color: shellColors.ivory,
    fontSize: 11,
    fontWeight: '900',
  },
  planTitle: {
    color: shellColors.ivory,
    fontSize: 21,
    fontWeight: '900',
    marginBottom: 6,
  },
  planDescription: {
    color: shellColors.muted,
    fontSize: 14,
    lineHeight: 20,
  },
  planPrice: {
    color: shellColors.gold,
    fontSize: 32,
    fontWeight: '900',
  },
  planPeriod: {
    color: shellColors.muted,
    marginLeft: 5,
  },
  savingsBadge: {
    marginLeft: 8,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: shellColors.goldSoft,
  },
  savingsBadgeText: {
    color: shellColors.gold,
    fontSize: 11,
    fontWeight: '900',
  },
  introCard: {
    padding: 12,
    borderRadius: 14,
    marginBottom: 14,
    backgroundColor: shellColors.goldSoft,
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  introText: {
    color: shellColors.ivory,
    fontSize: 13,
    fontWeight: '700',
  },
  selectedText: {
    color: shellColors.gold,
    fontWeight: '900',
    marginLeft: 5,
  },
  restoreButton: {
    marginTop: 8,
    paddingVertical: 14,
  },
  restoreText: {
    textAlign: 'center',
    color: shellColors.gold,
    fontWeight: '800',
  },
  termsBlock: {
    marginTop: 18,
    marginBottom: 18,
  },
  termsText: {
    color: shellColors.muted,
    opacity: 0.78,
    textAlign: 'center',
    fontSize: 11,
    lineHeight: 17,
  },
  termsLink: {
    color: shellColors.gold,
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  subscribeDock: {
    padding: 16,
    backgroundColor: shellColors.panel,
    borderTopWidth: 1,
    borderTopColor: shellColors.line,
  },
  disclosureText: {
    color: shellColors.muted,
    textAlign: 'center',
    fontSize: 12,
    lineHeight: 18,
    marginBottom: 12,
  },
  subscribeButton: {
    borderRadius: 999,
    backgroundColor: shellColors.gold,
  },
  subscribeButtonText: {
    color: '#24123F',
    fontWeight: '900',
    textAlign: 'center',
    fontSize: 18,
  },
});
